import { Box, Button, Typography, Grid, Skeleton } from '@mui/material';

import { Maybe } from '@/types';
import { BlogDetailsPage } from '@/types/generated';

import BlogListItem, { ICardDesignType } from '../BlogListItem';
import BlogListPagination from '../BlogListPagination/BlogListPagination';

import {
	blogListContainerStyles,
	getBlogListItemContainerStyles,
	showMoreBoxStyles,
} from './BlogListPageDefault.styles';

export interface IBlogListPageDefaultProps {
	handleShowMoreClick: () => void;
	totalBlogs: number;
	displayShowMoreButton: boolean;
	blogsList: Array<BlogDetailsPage>;
	buttonLabel: string;
	loadMoreButtonLabel: string;
	countAcrossDesktop?: Maybe<number>;
	countAcrossMobile?: Maybe<number>;
	hasPagination?: Maybe<boolean>;
	handlePaginationChangeEvent: (e: React.ChangeEvent<unknown>, page: number) => void;
	limit?: Maybe<number>;
	cardDesignType?: ICardDesignType;
	displayType?: Maybe<string>;
	isLoading?: boolean;
	currentPage?: number;
}

const BlogListPageDefault = ({
	handleShowMoreClick,
	totalBlogs,
	displayShowMoreButton,
	blogsList,
	buttonLabel,
	loadMoreButtonLabel,
	cardDesignType,
	countAcrossDesktop,
	countAcrossMobile,
	hasPagination,
	limit,
	displayType,
	handlePaginationChangeEvent,
	isLoading,
	currentPage,
}: IBlogListPageDefaultProps) => {
	const isFeatured = displayType?.toLowerCase() === 'featured';

	const hasLoadedBlogs = blogsList.length > 0;
	const hasBlogs = isLoading ? !!totalBlogs || hasLoadedBlogs : hasLoadedBlogs;
	const showNoBlogsMessage = !hasBlogs && !isLoading;
	const desktopBlogCount = showNoBlogsMessage ? 1 : countAcrossDesktop;
	const mobileBlogCount = showNoBlogsMessage ? 1 : countAcrossMobile;

	const testId = isFeatured ? 'blog_list_items_container_featured' : 'blog_list_items_container';

	const showLoading = isLoading && !hasLoadedBlogs;

	return (
		<Grid container sx={blogListContainerStyles} data-testid="blog_list_container">
			<Grid data-testid={testId} container sx={getBlogListItemContainerStyles(desktopBlogCount, mobileBlogCount)}>
				{showLoading && (
					<Box
						data-testid="blog_list_loading_message_container"
						sx={{
							width: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							paddingY: '2rem',
						}}
					>
						<Skeleton variant="rectangular" width="100%" height={200} />
					</Box>
				)}
				{showNoBlogsMessage && (
					<Box
						data-testid="blog_list_no_blogs_message_container"
						sx={{
							width: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							paddingY: '2rem',
						}}
					>
						<Typography component="p" variant="bodyLargeBook" data-testid="blog_list_no_blogs_message">
							There are no blogs to show.
						</Typography>
					</Box>
				)}
				{hasLoadedBlogs &&
					blogsList?.map(
						(item) =>
							item && (
								<BlogListItem
									blogDetails={item}
									buttonLabel={buttonLabel}
									key={item._id}
									cardDesignType={cardDesignType}
								/>
							)
					)}
			</Grid>
			{!hasPagination && displayShowMoreButton && (
				<Box sx={showMoreBoxStyles} data-testid="blog_list_show_more_container">
					<Typography variant="bodyLargeBook">
						Showing {blogsList.length} of {totalBlogs}
					</Typography>
					<Button
						size="L"
						variant="primaryDefault"
						onClick={handleShowMoreClick}
						data-testid="blog_list_show_more_button"
					>
						{loadMoreButtonLabel}
					</Button>
				</Box>
			)}
			{hasPagination && !showNoBlogsMessage && (
				<Box sx={showMoreBoxStyles} data-testid="blog_list_pagination_container">
					<BlogListPagination
						totalBlogs={totalBlogs}
						countPerPage={limit}
						handlePaginationChangeEvent={handlePaginationChangeEvent}
						currentPage={currentPage}
					/>
				</Box>
			)}
		</Grid>
	);
};

export default BlogListPageDefault;
